import React, { useEffect, useRef, useState } from 'react';
import { InputChevron, Label, Modal, SparkModalPortal, Text } from '@spark';
import TagInput from '@/shared/components/TagInput';
import UserRoleSelect, { INVITE_ROLE_OPTIONS } from './UserRoleSelect.jsx';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_SPLIT = /[,\s]+/;
const DEFAULT_INVITE_ROLE = 'Editor';

export default function AddUserModal({ open, onCancel, onSubmit, errorMessage }) {
    const emailsInputRef = useRef(null);
    const [emails, setEmails] = useState([]);
    const [rolesByEmail, setRolesByEmail] = useState({});
    const [emailsError, setEmailsError] = useState('');

    const resetForm = () => {
        setEmails([]);
        setRolesByEmail({});
        setEmailsError('');
    };

    useEffect(() => {
        if (open) resetForm();
    }, [open]);

    const handleEmailsChange = (nextEmails) => {
        setEmails(nextEmails);
        setRolesByEmail((prev) => {
            const next = {};
            nextEmails.forEach((email) => {
                next[email] = prev[email] || DEFAULT_INVITE_ROLE;
            });
            return next;
        });
        if (emailsError) setEmailsError('');
    };

    const handleCancel = () => {
        resetForm();
        onCancel?.();
    };

    const handleOk = () => {
        const flushed = emailsInputRef.current?.flush?.() ?? emails;
        if (!flushed?.length) {
            setEmailsError('Please enter an email address');
            return;
        }
        const invalid = flushed.filter((email) => !EMAIL_RE.test(email));
        if (invalid.length) {
            setEmailsError(
                invalid.length === 1
                    ? 'Please enter a valid email address'
                    : `Invalid emails: ${invalid.join(', ')}`
            );
            return;
        }
        setEmailsError('');
        onSubmit?.({
            invites: flushed.map((email) => ({
                email,
                role: rolesByEmail[email] || DEFAULT_INVITE_ROLE,
            })),
        });
    };

    return (
        <SparkModalPortal
            open={open}
            onClose={handleCancel}
            size="slot"
            destroyOnClose
        >
            <Modal
                open
                title="Invite your team"
                showSubtitle={false}
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                className="settings-add-user-modal"
                primaryLabel="Send Invites"
                tertiaryLabel="Cancel"
                primaryDisableWhenReadOnly
                onPrimaryClick={handleOk}
                onTertiaryClick={handleCancel}
                onCloseClick={handleCancel}
            >
                <div className="settings-add-user-form">
                    <div className="settings-add-user-form__intro">
                        <Text
                            as="p"
                            variant="base-regular"
                            color="secondary"
                            className="settings-add-user-form__hint"
                        >
                            You can paste multiple email addresses to invite everyone at once
                        </Text>
                        <TagInput
                            ref={emailsInputRef}
                            value={emails}
                            onChange={handleEmailsChange}
                            label="Invite by email"
                            showLabel
                            showHint={false}
                            splitPattern={EMAIL_SPLIT}
                            commitKeys={['Enter', ',', ' ']}
                            placeholder="Email, separated by comma or space"
                            ariaLabel="Invite by email"
                            error={emailsError}
                            suffix={emails.length ? <InputChevron /> : null}
                        />
                    </div>
                    {emails.length ? (
                        <div className="settings-invite-roles">
                            <Label label="Assign a role" />
                            <div className="settings-invite-roles__list">
                                {emails.map((email) => (
                                    <div key={email} className="settings-invite-roles__row">
                                        <Text
                                            as="p"
                                            variant="base-regular"
                                            color="primary"
                                            className="settings-invite-roles__email"
                                        >
                                            {email}
                                        </Text>
                                        <UserRoleSelect
                                            role={rolesByEmail[email] || DEFAULT_INVITE_ROLE}
                                            roles={INVITE_ROLE_OPTIONS}
                                            onChange={(nextRole) => {
                                                setRolesByEmail((prev) => ({
                                                    ...prev,
                                                    [email]: nextRole,
                                                }));
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {errorMessage ? (
                        <Text
                            as="p"
                            variant="detail-regular"
                            className="settings-add-user-form__error"
                            role="alert"
                        >
                            {errorMessage}
                        </Text>
                    ) : null}
                </div>
            </Modal>
        </SparkModalPortal>
    );
}
